/* eslint-disable react/jsx-indent */
import { Box, IconButton, makeStyles, Tooltip } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import ListAltIcon from '@material-ui/icons/ListAlt';
import VisibilityIcon from '@material-ui/icons/Visibility';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import React, { useCallback, useMemo } from 'react';
import { OperationStatus } from 'src/app/models/business/OperationStatus';
import { OperationOrderGateway } from 'src/app/models/business/OpertationOrderGateway';
import { MARKETPLACE, ORDER_STATE_LABELS_ES } from 'src/commons/const';
import { FormRadioNoFormOption } from 'src/lib/templates/components/FormRadioNoForm';
import { primary } from 'src/theme/colors';
import { Order } from '../../../../app/models/business/Order';
import {
  customFormDialog,
  isCatamarcaUser,
  OPERATION_ORDER_STATE,
  PAYMENT_REFERENCE,
  useFetch,
} from '../../../../commons';
import { InfoTableColumn } from '../../../../commons/types';
import { useRolesContext } from '../../../../context';
import { formatPrice } from '../../../../utils';
import { OrderDetailView } from '../components/OrderDetail';
import { OrderLogsOperation } from '../components/OrderLogsOperation/OrderLogsOperation';
import { getPaymentStateLabelEs } from '../utils/paymentsState';

const useStyles = makeStyles(() => ({
  iconColor: { color: primary.paleBlueStrong },
  iconButton: {
    padding: 0,
  },
  iconDisabled: { color: primary.disabled },
}));

interface UseOrderColumnsProps {
  updateOrder: (order_id: string, order: Order) => void;
  reLoad: () => void;
}

const READ_ONLY_STATES = [
  OPERATION_ORDER_STATE.CS_YELLOW,
  OPERATION_ORDER_STATE.CS_RED,
  OPERATION_ORDER_STATE.CS_OTHER,
];

export const useOrderColumns = ({ updateOrder, reLoad }: UseOrderColumnsProps) => {
  const { userInfo, selectedRole } = useRolesContext();
  const { enqueueSnackbar } = useSnackbar();
  const rolesEnableCatamarca = isCatamarcaUser(selectedRole.roleUserId.toString());
  const classes = useStyles();
  const { data: orderStatusSource } = useFetch<OperationStatus[]>(
    '/operation/status',
    'available=true',
  );

  // Se filtra solo por Stripe para este ecommers
  const { data: operationOrderGateway = [] } = useFetch<OperationOrderGateway[]>(
    '/operation/payment_gateway',
    '',
  );

  const hadlerViewOperation = useCallback(
    (row: Order) => {
      const statusPayments = orderStatusSource || [];
      const operationOrderGateways: FormRadioNoFormOption[] =
        operationOrderGateway?.map(({ payment_gateway_id, name }: OperationOrderGateway) => ({
          id: payment_gateway_id.toString(),
          name,
        })) || [];

      if (
        !orderStatusSource?.find(
          (item) => item.operation_order_state_id === row.payment.payment_state.payment_state_id,
        )
      ) {
        statusPayments.push({
          operation_order_state_id: row.payment.payment_state.payment_state_id,
          name: row.payment.payment_state.name,
          available: false,
          is_last_value: false,
        });
      }
      customFormDialog.show({
        title: 'Detalle de la Orden',
        renderComponent: (
          <OrderDetailView
            order={row}
            userInfo={userInfo}
            userRole={selectedRole}
            view
            notistick={enqueueSnackbar}
            statusPayments={statusPayments}
            reLoad={reLoad}
            operationOrderGateway={operationOrderGateways}
          />
        ),
        sizeWidth: 'md',
      });
    },
    // TODO verify this
    [userInfo, orderStatusSource, selectedRole, enqueueSnackbar, reLoad, operationOrderGateway],
  );

  const hadlerEditOperation = useCallback(
    (row: Order) => {
      const statusPayments =
        orderStatusSource?.map((status) => ({
          ...status,
        })) || [];
      const operationOrderGateways: FormRadioNoFormOption[] =
        operationOrderGateway?.map(({ payment_gateway_id, name }: OperationOrderGateway) => ({
          id: payment_gateway_id.toString(),
          name,
        })) || [];
      if (
        !orderStatusSource?.find(
          (item) => item.operation_order_state_id === row.payment.payment_state.payment_state_id,
        )
      ) {
        statusPayments.push({
          operation_order_state_id: row.payment.payment_state.payment_state_id,
          name: row.payment.payment_state.name,
          available: false,
          is_last_value: false,
        });
      }
      customFormDialog.show({
        title: 'Editar Orden',
        renderComponent: (
          <OrderDetailView
            order={row}
            userInfo={userInfo}
            userRole={selectedRole}
            updateOrder={updateOrder}
            notistick={enqueueSnackbar}
            statusPayments={statusPayments}
            reLoad={reLoad}
            operationOrderGateway={operationOrderGateways}
          />
        ),
        sizeWidth: 'md',
      });
    },
    // TODO same, verify this
    [
      userInfo,
      updateOrder,
      orderStatusSource,
      selectedRole,
      enqueueSnackbar,
      reLoad,
      operationOrderGateway,
    ],
  );
  const handlerLogsOperation = useCallback((row: Order) => {
    customFormDialog.show({
      title: 'Historial Orden',
      renderComponent: <OrderLogsOperation order={row} />,
      sizeWidth: 'md',
    });
  }, []);

  return useMemo<InfoTableColumn<Order>[]>(
    () => [
      {
        id: 'payment.payment_id',
        label: 'ID Pago',
        width: '5%',
        render: (value, row) => (
          <Tooltip title={row.payment.payment_id} arrow>
            <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              {`${row.payment.payment_id.slice(0, 5)}...`}
              <IconButton
                size="small"
                onClick={() => navigator.clipboard.writeText(row.payment.payment_id)}
              >
                <FileCopyIcon fontSize="small" style={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        ),
      },
      {
        id: 'order.order_id',
        label: 'ID Orden',
        width: '5%',
        render: (value, row) => (
          <Tooltip title={row.order_id} arrow>
            <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              {`${row.order_id.slice(0, 5)}...`}
              <IconButton size="small" onClick={() => navigator.clipboard.writeText(row.order_id)}>
                <FileCopyIcon fontSize="small" style={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        ),
      },
      {
        id: 'marketplace_id',
        label: 'Marketplace',
        width: '5%',
        orderById: 'marketplace.name',
        render: (value, row) => row.marketplace.name || 'N/A',
      },
      {
        id: 'buyer.name',
        label: 'Nombre',
        width: '5%',
        render: (value, row) => row.buyer.name || 'N/A',
      },
      {
        id: 'buyer.lastname',
        label: 'Apellido',
        width: '5%',
        render: (value, row) => row.buyer.lastname || 'N/A',
      },
      {
        id: 'buyer.metadata.cuil',
        label: 'Cuil',
        width: '5%',
        render: (value, row) => row.buyer?.metadata?.cuil || 'N/A',
      },
      {
        id: 'tracking_number',
        label: 'Nro. Seguimiento',
        width: '5%',
        render: (value, row) => row.tracking_number || 'N/A',
      },
      {
        id: 'order.retail',
        label: 'Retail',
        width: '3%',
        orderById: 'retail',
        render: (value, row) => {
          return row.retail_name ?? '—';
        },
      },
      // Comentado para que quede mejor la tabla
      // Falta agregar esta info en el detalle
      // {
      //   id: 'buyer.name',
      //   label: 'Comprador',
      //   width: '10%',
      //   render: (value, row) => `${row.buyer.name} ${row.buyer.lastname}`,
      // },
      // {
      //   id: 'buyer.document_number',
      //   label: 'Documento',
      //   width: '2%',
      //   render: (value, row) => `${row.buyer.document_number}`,
      // },
      {
        id: 'buyer.email',
        label: 'Email',
        width: '5%',
        headerAlign: 'center',
        render: (value, row) => `${row.buyer.email}`,
      },
      {
        id: 'buyer.phone_number',
        label: 'Telefono',
        width: '10%',
        render: (value, row) => `${row.buyer.phone_number}`,
      },
      {
        id: 'order.created_at',
        label: 'Fecha de Creacion',
        width: '10%',
        render: (value, row) => {
          const date = moment(row.created_at).format('DD-MM-YYYY');
          const time = moment(row.created_at).format('HH:mm:ss');
          return (
            <>
              {date}
              <br />
              {time}
            </>
          );
        },
      },
      {
        id: 'order.last_update',
        label: 'Ultima Actualización',
        width: '10%',
        render: (value, row) => {
          if (!row.operation?.updated_at) return '';
          const date = moment(row.operation.updated_at).format('DD-MM-YYYY');
          const time = moment(row.operation.updated_at).format('HH:mm:ss');

          return (
            <>
              {date}
              <br />
              {time}
            </>
          );
        },
      },
      {
        id: 'order.capture_before',
        label: 'Vto. de Hold',
        width: '10%',
        render: (value, row) => {
          const timestamp = row.payment.metadata_response?.capture_before;
          if (!timestamp) return 'N/A';

          // Convertir a milisegundos si viene en segundos
          const captureDate = moment(timestamp < 1e10 ? timestamp * 1000 : timestamp);
          const now = moment();
          const daysDiff = captureDate.diff(now, 'days', true); // diferencia con decimales

          const bgColor = captureDate.isBefore(now)
            ? 'rgba(239, 154, 154, 0.7)' // rojo
            : daysDiff < 1
            ? 'rgba(255, 183, 77, 0.7)' // naranja
            : daysDiff < 3
            ? 'rgba(255, 241, 118, 0.7)' // amarillo
            : 'rgba(165, 214, 167, 0.7)'; // verde

          return (
            <Box
              px={1}
              py={0.5}
              borderRadius="12px"
              style={{
                backgroundColor: bgColor,
                display: 'inline-block',
              }}
            >
              <>
                {captureDate.format('DD-MM-YYYY')}
                <br />
                {captureDate.format('HH:mm:ss')}
              </>
            </Box>
          );
        },
      },
      {
        id: 'order.total',
        label: 'Importe',
        width: '2%',
        render: (value, row) => formatPrice(row.total),
      },
      {
        id: 'payment_state.payment_state_id',
        label: 'Estado',
        width: '10%',
        render: (value, row) => getPaymentStateLabelEs(row.payment.payment_state.name ?? ''),
      },
      {
        id: 'order.order_id',
        label: 'Estado orden',
        width: '10%',
        render: (value, row) => {
          const orderStateName = row.order_state?.name ?? '';
          return ORDER_STATE_LABELS_ES[orderStateName] || orderStateName;
        },
      },
      {
        id: 'assigned_to',
        label: 'Asignado',
        width: '10%',
        orderById: 'operation.assigned_to.username',
        render: (value, row) => row.operation?.assigned_to.username || 'No asignada',
      },
      {
        id: 'prmotion_id',
        label: 'Promoción',
        width: '10%',
        orderById: 'payment_promotion.name',
        render: (value, row) => row.payment.promotion?.name || 'N/A',
      },
      {
        id: 'icono',
        label: '',
        width: '5%',
        hideSortIcon: true,
        style: { textAlign: 'right' },
        render: (value: any, row) => {
          return (
            <Box display="flex" alignItems="space-between" justifyContent="flex-end" width="100%">
              <IconButton
                className={classes.iconButton}
                onClick={() => hadlerViewOperation(row)}
                style={{ marginRight: '8px' }}
              >
                <VisibilityIcon className={classes.iconColor} />
              </IconButton>

              {((rolesEnableCatamarca &&
                row.marketplace.marketplace_id === MARKETPLACE.PIPESTORE &&
                row.payment.reference === PAYMENT_REFERENCE.CREDIT) ||
                row.marketplace.marketplace_id !== MARKETPLACE.PIPESTORE ||
                (row.marketplace.marketplace_id === MARKETPLACE.PIPESTORE &&
                  row.payment.reference !== PAYMENT_REFERENCE.CREDIT)) && (
                <IconButton
                  className={classes.iconButton}
                  onClick={() => hadlerEditOperation(row)}
                  style={{ marginRight: '8px' }}
                >
                  <EditIcon className={classes.iconColor} />
                </IconButton>
              )}

              <IconButton
                className={classes.iconButton}
                onClick={() => handlerLogsOperation(row)}
                style={{ marginRight: '8px' }}
              >
                <ListAltIcon className={classes.iconColor} />
              </IconButton>
            </Box>
          );
        },
      },
    ],
    [
      classes.iconButton,
      classes.iconColor,
      rolesEnableCatamarca,
      hadlerViewOperation,
      hadlerEditOperation,
      handlerLogsOperation,
    ],
  );
};
